import { create } from "zustand";
import {
  type PolicyListCursor,
  type PolicyListRule,
} from "#app/utils/policy-list";

type PolicyListStore = {
  rules: PolicyListRule[];
  loading: boolean;
  listError: string | null;
  cursor: PolicyListCursor | null;
  loadingMore: boolean;
  search: string;
  /** Shared across sidebar + page so concurrent loads clear loading correctly. */
  loadGeneration: number;
  beginLoad: () => number;
  setSearch: (search: string) => void;
  setRules: (rules: PolicyListRule[]) => void;
  updateRules: (updater: (rules: PolicyListRule[]) => PolicyListRule[]) => void;
  appendRules: (rules: PolicyListRule[]) => void;
  setLoading: (loading: boolean) => void;
  setListError: (error: string | null) => void;
  setCursor: (cursor: PolicyListCursor | null) => void;
  setLoadingMore: (loading: boolean) => void;
};

export const usePolicyListStore = create<PolicyListStore>((set, get) => ({
  rules: [],
  loading: true,
  listError: null,
  cursor: null,
  loadingMore: false,
  search: "",
  loadGeneration: 0,
  beginLoad: () => {
    const loadGeneration = get().loadGeneration + 1;
    set({ loadGeneration, loading: true, listError: null });
    return loadGeneration;
  },
  setSearch: (search) =>
    set((state) => (state.search === search ? state : { search })),
  setRules: (rules) => set({ rules }),
  updateRules: (updater) => set((state) => ({ rules: updater(state.rules) })),
  appendRules: (rules) =>
    set((state) => {
      const seen = new Set(state.rules.map((r) => r.publicId));
      return {
        rules: [...state.rules, ...rules.filter((r) => !seen.has(r.publicId))],
      };
    }),
  setLoading: (loading) => set({ loading }),
  setListError: (listError) => set({ listError }),
  setCursor: (cursor) => set({ cursor }),
  setLoadingMore: (loadingMore) => set({ loadingMore }),
}));
